import { useCallback, useEffect, useRef, useState } from 'react';
import useScript from 'app/hooks/useScript';

export interface UseAgaveLinkProps {
  linkToken?: string;
  referenceId?: string;
  onSuccess: (publicToken: string) => void;
  onExit?: (error?: string) => void;
  showSandboxSourceSystems?: boolean;
  showProductionSourceSystems?: boolean;
  sourceSystem?: string | string[];
  sourceSystemEnvironment?: string;
  sourceSystemOptions?: Record<string, unknown>;
  category?: string;
  primaryColor?: string;
}

export interface UseAgaveLinkResponse {
  openLink: () => void;
  isReady: boolean;
  error: ErrorEvent | null;
}

interface AgaveLink {
  openLink: (config: UseAgaveLinkProps) => void;
}

declare global {
  interface Window {
    AgaveLink?: AgaveLink;
  }
}

const AGAVE_CDN_URL = 'https://cdn.agaveapi.com/init.js';

export const useAgaveLink = (config: UseAgaveLinkProps): UseAgaveLinkResponse => {
  const [loading, error] = useScript({
    src: AGAVE_CDN_URL,
    checkForExisting: true,
  });
  const [isReady, setIsReady] = useState(false);
  const isServer = typeof window === 'undefined';
  const isReadyForInitialization = !isServer && !!window.AgaveLink && !loading && !error;

  useEffect(() => {
    if (isReadyForInitialization && window.AgaveLink) {
      setIsReady(true);
    }
  }, [isReadyForInitialization]);

  const configRef = useRef(config);
  configRef.current = config;

  const openLink = useCallback(() => {
    if (window.AgaveLink) {
      window.AgaveLink.openLink(configRef.current);
    }
  }, []);

  return { openLink, isReady, error };
};
